import React, { useState } from "react";
import {
  Badge,
  Button,
  Calendar,
  Card,
  Divider,
  Form,
  Input,
  InputNumber,
  List,
  message,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Table,
  Tag
} from "antd";
import { CalendarOutlined, DeleteOutlined, EditOutlined, PlusOutlined, TableOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFirestore } from "react-redux-firebase";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import locale from "antd/es/date-picker/locale/nl_NL";

const OrderCapacity = () => {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const [form] = Form.useForm();
  const [limitForm] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [limitModalVisible, setLimitModalVisible] = useState(false);
  const [editingCapacity, setEditingCapacity] = useState(null);
  const [editingLimit, setEditingLimit] = useState(null);
  const [editingLimitIndex, setEditingLimitIndex] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState("calendar");
  const orderCapacity = useSelector((state) => state.firestoreReducer.ordered.orderCapacity);

  // Create a map of dates to capacity for quick lookup
  const capacityMap = {};
  if (orderCapacity) {
    orderCapacity.forEach((capacity) => {
      capacityMap[capacity.date] = capacity;
    });
  }

  const getCapacityStatus = (date) => {
    const dateStr = date.format(DATE_FORMAT_DD_MM_YYYY);
    const capacity = capacityMap[dateStr];

    if (!capacity || !capacity.limits || capacity.limits.length === 0) {
      return { type: "default", text: t("no_capacities_set"), color: "gray", hasLimits: false };
    }

    // Check if any limit is full or almost full
    let isFull = false;
    let isAlmostFull = false;

    capacity.limits.forEach((limit) => {
      const percentage = (limit.currentAmount / limit.limit) * 100;
      if (percentage >= 100) {
        isFull = true;
      } else if (percentage >= 80) {
        isAlmostFull = true;
      }
    });

    if (isFull) {
      return { type: "error", text: t("day_full"), color: "red", hasLimits: true };
    }
    if (isAlmostFull) {
      return { type: "warning", text: t("almost_full"), color: "orange", hasLimits: true };
    }
    return { type: "success", text: t("day_limited"), color: "blue", hasLimits: true };
  };

  const openModal = (capacity = null, date = null) => {
    setEditingCapacity(capacity);
    setSelectedDate(date);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingCapacity(null);
    setSelectedDate(null);
  };

  const openLimitModal = (limit = null, index = null) => {
    setEditingLimit(limit);
    setEditingLimitIndex(index);

    if (limit) {
      limitForm.setFieldsValue({
        deliveryMethod: limit.deliveryMethod,
        scope: limit.scope,
        limit: limit.limit,
        currentAmount: limit.currentAmount || 0,
        notificationTitle: limit.notificationTitle || "",
        notificationMessage: limit.notificationMessage || "",
      });
    } else {
      limitForm.resetFields();
      limitForm.setFieldsValue({
        currentAmount: 0,
      });
    }
    setLimitModalVisible(true);
  };

  const closeLimitModal = () => {
    setLimitModalVisible(false);
    setEditingLimit(null);
    setEditingLimitIndex(null);
    limitForm.resetFields();
  };

  const handleAddLimit = async () => {
    try {
      const values = await limitForm.validateFields();
      const dateStr = editingCapacity ? editingCapacity.date : selectedDate.format(DATE_FORMAT_DD_MM_YYYY);

      const newLimit = {
        deliveryMethod: values.deliveryMethod,
        scope: values.scope,
        limit: values.limit,
        currentAmount: values.currentAmount || 0,
        notificationTitle: values.notificationTitle || "",
        notificationMessage: values.notificationMessage || "",
      };

      let existingCapacity = capacityMap[dateStr];
      let updatedLimits = [];

      if (existingCapacity) {
        const limits = [...(existingCapacity.limits || [])];

        if (editingLimitIndex !== null) {
          limits[editingLimitIndex] = newLimit;
        } else {
          limits.push(newLimit);
        }

        updatedLimits = limits;

        await firestore.collection("orderCapacity").doc(existingCapacity.id).update({
          limits: updatedLimits,
          updatedAt: dayjs().toISOString(),
        });

        // Update local state
        setEditingCapacity({
          ...existingCapacity,
          limits: updatedLimits,
          updatedAt: dayjs().toISOString(),
        });
      } else {
        updatedLimits = [newLimit];

        const docRef = await firestore.collection("orderCapacity").add({
          date: dateStr,
          limits: updatedLimits,
          createdAt: dayjs().toISOString(),
          updatedAt: dayjs().toISOString(),
        });

        // Update local state with new document
        setEditingCapacity({
          id: docRef.id,
          date: dateStr,
          limits: updatedLimits,
          createdAt: dayjs().toISOString(),
          updatedAt: dayjs().toISOString(),
        });
      }

      message.success(t("capacity_saved"));
      closeLimitModal();
    } catch (error) {
      console.error("Error saving capacity limit:", error);
    }
  };

  const handleDeleteLimit = async (index) => {
    try {
      if (!editingCapacity) return;

      const limits = [...editingCapacity.limits];
      limits.splice(index, 1);

      if (limits.length === 0) {
        await firestore.collection("orderCapacity").doc(editingCapacity.id).delete();
        closeModal();
      } else {
        await firestore.collection("orderCapacity").doc(editingCapacity.id).update({
          limits,
          updatedAt: dayjs().toISOString(),
        });

        // Update local state
        setEditingCapacity({
          ...editingCapacity,
          limits,
          updatedAt: dayjs().toISOString(),
        });
      }

      message.success(t("capacity_deleted"));
    } catch (error) {
      console.error("Error deleting capacity limit:", error);
    }
  };

  const dateCellRender = (date) => {
    const dateStr = date.format(DATE_FORMAT_DD_MM_YYYY);
    const capacity = capacityMap[dateStr];
    const status = getCapacityStatus(date);

    return (
      <div
        style={{
          minHeight: 60,
          padding: 4,
          cursor: "pointer",
          borderRadius: 4,
          transition: "background-color 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
      >
        <Badge status={status.type} text={status.text} />
        {capacity && capacity.limits && capacity.limits.length > 0 && (
          <div style={{ fontSize: 10, marginTop: 4 }}>
            {capacity.limits.map((limit, idx) => (
              <div key={idx} style={{ marginTop: 2 }}>
                <Tag
                  color={
                    limit.currentAmount >= limit.limit
                      ? "red"
                      : limit.currentAmount / limit.limit >= 0.8
                        ? "orange"
                        : "blue"
                  }
                  style={{ fontSize: 9, padding: "0 4px" }}
                >
                  {t(limit.deliveryMethod === "pickup" ? "pickup" : "send")} {t(limit.scope)}: {limit.currentAmount}/
                  {limit.limit}
                </Tag>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const onCalendarSelect = (date, info) => {
    // Only open modal when clicking on a date cell, not when changing month/year
    if (info?.source === "date") {
      const dateStr = date.format(DATE_FORMAT_DD_MM_YYYY);
      const capacity = capacityMap[dateStr];
      if (capacity) {
        openModal(capacity);
      } else {
        openModal(null, date);
      }
    }
  };

  const headerRender = ({ value, type, onChange, onTypeChange }) => {
    const month = value.month();
    const year = value.year();
    const monthOptions = [];
    const yearOptions = [];

    for (let i = 0; i < 12; i++) {
      monthOptions.push(
        <Select.Option key={i} value={i}>
          {dayjs().month(i).format("MMMM")}
        </Select.Option>,
      );
    }

    const currentYear = dayjs().year();
    for (let i = currentYear - 1; i <= currentYear + 2; i++) {
      yearOptions.push(
        <Select.Option key={i} value={i}>
          {i}
        </Select.Option>,
      );
    }

    return (
      <div style={{ padding: "16px 24px", background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, color: "#262626" }}>
          {value.format("MMMM YYYY")}
        </div>
        <Space>
          <Select
            value={month}
            onChange={(newMonth) => {
              const now = value.clone().month(newMonth);
              onChange(now);
            }}
            style={{ width: 140 }}
          >
            {monthOptions}
          </Select>
          <Select
            value={year}
            onChange={(newYear) => {
              const now = value.clone().year(newYear);
              onChange(now);
            }}
            style={{ width: 100 }}
          >
            {yearOptions}
          </Select>
        </Space>
      </div>
    );
  };

  const columns = [
    {
      title: t("date"),
      dataIndex: "date",
      key: "date",
      render: (text) => dayjs(text, DATE_FORMAT_DD_MM_YYYY).format("ddd D MMM 'YY"),
      sorter: (a, b) => dayjs(a.date, DATE_FORMAT_DD_MM_YYYY).unix() - dayjs(b.date, DATE_FORMAT_DD_MM_YYYY).unix(),
    },
    {
      title: t("capacities_for_this_day"),
      key: "limits",
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {record.limits && record.limits.length > 0 ? (
            record.limits.map((limit, idx) => (
              <Tag
                key={idx}
                color={
                  limit.currentAmount >= limit.limit
                    ? "red"
                    : limit.currentAmount / limit.limit >= 0.8
                      ? "orange"
                      : "blue"
                }
              >
                {t(limit.deliveryMethod === "pickup" ? "pickup" : "send")} {t(limit.scope)}: {limit.currentAmount}/
                {limit.limit}
              </Tag>
            ))
          ) : (
            <span>-</span>
          )}
        </Space>
      ),
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <Button type="link" onClick={() => openModal(record)}>
          {t("edit")}
        </Button>
      ),
    },
  ];

  const getDeliveryMethodLabel = (method) => {
    return method === "pickup" ? t("pickup") : t("send");
  };

  return (
    <Card>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h2 style={{ margin: 0 }}>{t("capacity_management")}</h2>
        <Segmented
          value={viewMode}
          onChange={setViewMode}
          options={[
            {
              label: t("calendar_view"),
              value: "calendar",
              icon: <CalendarOutlined />,
            },
            {
              label: t("list_view"),
              value: "list",
              icon: <TableOutlined />,
            },
          ]}
        />
      </div>

      {viewMode === "calendar" ? (
        <Calendar
          dateCellRender={dateCellRender}
          onSelect={onCalendarSelect}
          headerRender={headerRender}
          locale={locale}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={orderCapacity}
          rowKey="id"
          locale={{ emptyText: t("table_no_capacities") }}
        />
      )}

      {/* Main Modal - Shows all limits for a date */}
      <Modal
        title={
          editingCapacity
            ? `${t("capacity_for_date")} ${dayjs(editingCapacity.date, DATE_FORMAT_DD_MM_YYYY).format("D MMMM YYYY")}`
            : selectedDate
              ? `${t("capacity_for_date")} ${selectedDate.format("D MMMM YYYY")}`
              : t("edit_capacity")
        }
        open={modalVisible}
        onCancel={closeModal}
        footer={[
          <Button key="close" onClick={closeModal}>
            {t("close")}
          </Button>,
        ]}
        width={700}
      >
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openLimitModal()}>
            {t("add_capacity_limit")}
          </Button>
        </div>

        {editingCapacity && editingCapacity.limits && editingCapacity.limits.length > 0 ? (
          <List
            dataSource={editingCapacity.limits}
            renderItem={(limit, index) => (
              <List.Item
                actions={[
                  <Button
                    key="edit"
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => openLimitModal(limit, index)}
                  />,
                  <Popconfirm
                    key="delete"
                    title={t("delete_capacity")}
                    onConfirm={() => handleDeleteLimit(index)}
                    okText={t("yes")}
                    cancelText={t("no")}
                  >
                    <Button type="link" danger icon={<DeleteOutlined />} />
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Tag color="blue">
                        {getDeliveryMethodLabel(limit.deliveryMethod)} - {t(limit.scope)}
                      </Tag>
                      <Tag
                        color={
                          limit.currentAmount >= limit.limit
                            ? "red"
                            : limit.currentAmount / limit.limit >= 0.8
                              ? "orange"
                              : "green"
                        }
                      >
                        {limit.currentAmount}/{limit.limit}
                      </Tag>
                    </Space>
                  }
                  description={
                    limit.notificationTitle || limit.notificationMessage ? (
                      <div style={{ fontSize: 12 }}>
                        {limit.notificationTitle && <div>📢 {limit.notificationTitle}</div>}
                        {limit.notificationMessage && <div>{limit.notificationMessage}</div>}
                      </div>
                    ) : null
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>{t("no_capacities_set")}</div>
        )}
      </Modal>

      {/* Limit Modal - Add/Edit individual limit */}
      <Modal
        title={editingLimit ? t("edit_capacity") : t("add_capacity")}
        open={limitModalVisible}
        onOk={handleAddLimit}
        onCancel={closeLimitModal}
        okText={t("save")}
        cancelText={t("cancel")}
        width={500}
      >
        <Form form={limitForm} layout="vertical">
          <Form.Item
            name="deliveryMethod"
            label={t("delivery_method")}
            rules={[{ required: true, message: t("required") }]}
          >
            <Select>
              <Select.Option value="pickup">{t("pickup")}</Select.Option>
              <Select.Option value="send">{t("send")}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="scope" label={t("scope")} rules={[{ required: true, message: t("required") }]}>
            <Select>
              <Select.Option value="regional">{t("regional")}</Select.Option>
              <Select.Option value="national">{t("national")}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="limit" label={t("limit")} rules={[{ required: true, message: t("required") }]}>
            <InputNumber style={{ width: "100%" }} min={1} />
          </Form.Item>

          <Form.Item name="currentAmount" label={t("current_amount")}>
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>

          <Divider>{t("notification_when_full")}</Divider>

          <Form.Item name="notificationTitle" label={t("notification_title")}>
            <Input placeholder={t("notification_title")} />
          </Form.Item>

          <Form.Item name="notificationMessage" label={t("notification_message")}>
            <Input.TextArea rows={3} placeholder={t("notification_message")} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default OrderCapacity;
